import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, MenuItem, IconButton,
    Alert, CircularProgress, Snackbar, useTheme, useMediaQuery, Stack, Divider,
    FormControlLabel, Checkbox, List, ListItemButton, ListItemIcon, ListItemText,
    Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import NotesIcon from '@mui/icons-material/Notes';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import api from '../services/api';
import {
    consumirAutorizacao, temAutorizacao, solicitarAutorizacao, solicitarAutorizacaoSessao,
} from '../services/autorizacaoOperacao';
import { pedirMotivo, pedirSenha, toastSuccess, confirmAcao } from '../services/alerts';
import { registrarRotuloDeRota } from '../services/navegacao';
import { isVisualizador } from '../services/auth';

const NIVEL_RISCO_OPCOES = [
    { v: 'BAIXO', l: 'Baixo', color: '#16a34a', bg: '#dcfce7' },
    { v: 'MODERADO', l: 'Moderado', color: '#f59e0b', bg: '#fef3c7' },
    { v: 'ALTO', l: 'Alto', color: '#ea580c', bg: '#ffedd5' },
    { v: 'CRITICO', l: 'Crítico', color: '#dc2626', bg: '#fee2e2' },
];

const avaliacaoVazia = () => ({
    dataAvaliacao: new Date().toISOString().substring(0, 10),
    ideacaoSuicida: false,
    planoSuicida: false,
    ideacaoAutolesao: false,
    riscoParaTerceiros: false,
    condutaAdotada: '',
    encaminhamentosRealizados: '',
    redeSocialAcionada: '',
    nivelRisco: 'BAIXO',
});

const SECAO_EVOLUCAO = 'EVOLUCAO';
const SECAO_RISCO = 'RISCO';

// Status em que a sessao ja conta como atendimento finalizado.
const STATUS_FINALIZADOS = ['REALIZADA', 'REALIZADA_ALTERADA'];

export default function EvolucaoSessaoPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const soLeitura = isVisualizador();

    const [sessao, setSessao] = useState(null);
    const [secao, setSecao] = useState(SECAO_EVOLUCAO);
    const [modoFocoAtivo, setModoFocoAtivo] = useState(false);
    const [countdownModal, setCountdownModal] = useState({ open: false, segundos: 3 });
    const [evolucao, setEvolucao] = useState({ anotacoes: '', tecnicasUtilizadas: '', planejamentoProximaSessao: '' });
    const [avaliacoes, setAvaliacoes] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // C3: distingue "sessao sem evolucao" de "falha ao carregar". Enquanto a carga
    // falha, o formulario fica bloqueado — salvar em cima do que nao carregou
    // apagaria conteudo existente.
    const [falhaCarga, setFalhaCarga] = useState('');
    const [recarregar, setRecarregar] = useState(0);
    const [evolucaoSalva, setEvolucaoSalva] = useState(null);
    // Histórico da sessão: registro vigente + cadeia de anulados.
    const [historico, setHistorico] = useState([]);
    // Conflito de versão: o texto digitado NÃO é descartado; o usuário decide.
    const [conflito, setConflito] = useState(false);
    const [alteracoesNaoSalvas, setAlteracoesNaoSalvas] = useState(false);
    const [estadoSalvamento, setEstadoSalvamento] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const ativarFoco = () => {
        setModoFocoAtivo(true);
        window.dispatchEvent(new CustomEvent('sigapsi:modoFoco', { detail: true }));
    };
    const desativarFoco = () => {
        setModoFocoAtivo(false);
        window.dispatchEvent(new CustomEvent('sigapsi:modoFoco', { detail: false }));
    };

    useEffect(() => {
        return () => {
            window.dispatchEvent(new CustomEvent('sigapsi:modoFoco', { detail: false }));
        };
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data: ses } = await api.get(`/sessoes/${id}`);
                setSessao(ses);
                // Alimenta a trilha de navegacao: "Atendimentos > Maria > Evolucao"
                // em vez de "#5" ou "SigaPsi".
                if (ses?.nomePaciente) {
                    registrarRotuloDeRota(`sessao:${id}`, ses.nomePaciente);
                }
                try {
                    const { data: param } = await api.get('/parametros');
                    if (param?.modoFocoEvolucao) {
                        setCountdownModal({ open: true, segundos: 3 });
                    }
                } catch { /* ignora */ }
                try {
                    const resp = await api.get(`/sessoes/${id}/evolucao`);
                    const evo = resp.status === 204 ? null : resp.data;
                    setEvolucaoSalva(evo || null);
                    // Histórico (vigente + anuladas): mostra o que foi anulado sem
                    // apagar nada. Falha aqui não bloqueia a edição.
                    api.get(`/sessoes/${id}/evolucoes`)
                        .then(r => setHistorico(Array.isArray(r.data) ? r.data : []))
                        .catch(() => setHistorico([]));
                    // Registro anulado NÃO é reaproveitado: o formulário abre em
                    // branco para a evolução substituta, e o conteúdo anulado
                    // continua visível no histórico.
                    if (evo?.anulada) {
                        setEvolucao({ anotacoes: '', tecnicasUtilizadas: '', planejamentoProximaSessao: '' });
                        setAvaliacoes([]);
                    } else if (evo) {
                        setEvolucao({
                            anotacoes: evo.anotacoes || '',
                            tecnicasUtilizadas: evo.tecnicasUtilizadas || '',
                            planejamentoProximaSessao: evo.planejamentoProximaSessao || '',
                        });
                        setAvaliacoes((evo.avaliacaoRisco || []).map(a => ({
                            dataAvaliacao: a.dataAvaliacao ? String(a.dataAvaliacao).substring(0, 10) : new Date().toISOString().substring(0, 10),
                            ideacaoSuicida: !!a.ideacaoSuicida,
                            planoSuicida: !!a.planoSuicida,
                            ideacaoAutolesao: !!a.ideacaoAutolesao,
                            riscoParaTerceiros: !!a.riscoParaTerceiros,
                            condutaAdotada: a.condutaAdotada || '',
                            encaminhamentosRealizados: a.encaminhamentosRealizados || '',
                            redeSocialAcionada: a.redeSocialAcionada || '',
                            nivelRisco: a.nivelRisco || 'BAIXO',
                        })));
                    }
                } catch (e) {
                    // 404/204 = nao existe evolucao (form em branco e correto).
                    // Qualquer outra falha (rede, 500, 403) bloqueia a edicao.
                    const status = e?.response?.status;
                    if (status && status !== 404) {
                        setFalhaCarga(status === 403
                            ? 'Você não tem acesso à evolução desta sessão.'
                            : 'Não foi possível carregar a evolução já registrada.');
                    } else if (!status) {
                        setFalhaCarga('Sem resposta do servidor ao carregar a evolução.');
                    }
                }
            } catch {
                setError('Não foi possível carregar a sessão.');
            } finally {
                setFetching(false);
            }
        })();
        // recarregar entra na dependencia para o botao "Tentar de novo" refazer a carga
    }, [id, recarregar]);

    // Avisa antes de sair com alteracoes nao salvas (fechar aba/recarregar).
    useEffect(() => {
        if (!alteracoesNaoSalvas) return undefined;
        const aviso = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', aviso);
        return () => window.removeEventListener('beforeunload', aviso);
    }, [alteracoesNaoSalvas]);

    useEffect(() => {
        if (!countdownModal.open) return;
        if (countdownModal.segundos <= 0) {
            setCountdownModal({ open: false, segundos: 0 });
            ativarFoco();
            return;
        }
        const t = setTimeout(() => {
            setCountdownModal(prev => ({ ...prev, segundos: prev.segundos - 1 }));
        }, 1000);
        return () => clearTimeout(t);
    }, [countdownModal]);

    const marcarSujo = () => { setAlteracoesNaoSalvas(true); setEstadoSalvamento('Alterações não salvas'); };
    const setEvoCampo = (campo, valor) => { marcarSujo(); setEvolucao(prev => ({ ...prev, [campo]: valor })); };
    const setAvCampo = (idx, campo, valor) => { marcarSujo(); setAvaliacoes(prev => prev.map((a, i) => i === idx ? { ...a, [campo]: valor } : a)); };
    const addAvaliacao = () => { marcarSujo(); setAvaliacoes(prev => [...prev, avaliacaoVazia()]); };
    const removeAvaliacao = (idx) => { marcarSujo(); setAvaliacoes(prev => prev.filter((_, i) => i !== idx)); };

    const evolucaoPreenchida = !!(
        (evolucao.anotacoes && evolucao.anotacoes.trim()) ||
        (evolucao.tecnicasUtilizadas && evolucao.tecnicasUtilizadas.trim()) ||
        (evolucao.planejamentoProximaSessao && evolucao.planejamentoProximaSessao.trim())
    );
    const riscoPreenchido = avaliacoes.length > 0;

    /**
     * @param finalizar false = salvar rascunho (nao marca a sessao como realizada);
     *                  true  = finalizar atendimento.
     */
    const salvar = async (finalizar = false) => {
        setError('');

        if (falhaCarga) {
            setError('A evolução não foi carregada corretamente. Recarregue antes de salvar.');
            return;
        }

        // Registro ja finalizado (ou sessao finalizada) exige motivo da alteracao:
        // a API recusa retificacao sem motivo.
        const jaFinalizado = (evolucaoSalva && !evolucaoSalva.rascunho) || STATUS_FINALIZADOS.includes(sessao?.statusSessao);
        let motivoAlteracao = null;
        if (jaFinalizado) {
            motivoAlteracao = await pedirMotivo({
                title: 'Alterar registro finalizado',
                text: 'Este atendimento já foi finalizado. Descreva o motivo da retificação:',
                confirmButtonText: 'Salvar alteração',
            });
            if (!motivoAlteracao) return;
        }

        // Registro finalizado exige autorização temporária de uso único. Quando o
        // usuário chega direto nesta tela (painel do dia, link), o token não existe
        // ainda: pede a senha aqui em vez de devolver 403 sem saída.
        if (jaFinalizado && !temAutorizacao(`sessao:${id}:evolucao`)) {
            const senha = await pedirSenha();
            if (!senha) return;
            try {
                await solicitarAutorizacaoSessao(id, 'evolucao', senha);
            } catch (e) {
                setError(e?.response?.data?.message || 'Senha inválida.');
                return;
            }
        }

        setSaving(true);
        setEstadoSalvamento('Salvando…');
        try {
            const autorizacao = consumirAutorizacao(`sessao:${id}:evolucao`);
            const payload = {
                autorizacao: autorizacao || null,
                finalizarAtendimento: finalizar,
                motivoAlteracao,
                // Versao carregada: obrigatoria na ATUALIZACAO (o servidor recusa
                // sem ela). Criacao — primeira evolucao ou substituta de uma
                // anulada — nao tem versao anterior para comparar.
                version: (evolucaoSalva && !evolucaoSalva.anulada) ? evolucaoSalva.version : null,
                anotacoes: evolucao.anotacoes || null,
                tecnicasUtilizadas: evolucao.tecnicasUtilizadas || null,
                planejamentoProximaSessao: evolucao.planejamentoProximaSessao || null,
                avaliacaoRisco: avaliacoes.map(a => ({
                    dataAvaliacao: a.dataAvaliacao || null,
                    ideacaoSuicida: !!a.ideacaoSuicida,
                    planoSuicida: !!a.planoSuicida,
                    ideacaoAutolesao: !!a.ideacaoAutolesao,
                    riscoParaTerceiros: !!a.riscoParaTerceiros,
                    condutaAdotada: a.condutaAdotada || null,
                    encaminhamentosRealizados: a.encaminhamentosRealizados || null,
                    redeSocialAcionada: a.redeSocialAcionada || null,
                    nivelRisco: a.nivelRisco || 'BAIXO',
                })),
            };
            const { data } = await api.post(`/sessoes/${id}/evolucao`, payload);
            setEvolucaoSalva(data || null);
            setConflito(false);
            api.get(`/sessoes/${id}/evolucoes`)
                .then(r => setHistorico(Array.isArray(r.data) ? r.data : []))
                .catch(() => { });
            setAlteracoesNaoSalvas(false);
            setEstadoSalvamento(finalizar ? 'Atendimento finalizado' : 'Rascunho salvo');
            setSnackbar({
                open: true,
                message: finalizar ? 'Atendimento finalizado!' : 'Rascunho salvo.',
                severity: 'success',
            });
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data;
            // 409 = outra pessoa gravou antes. O texto digitado FICA na tela: o
            // usuário compara e decide recarregar, em vez de perder o que escreveu.
            if (err?.response?.status === 409) setConflito(true);
            setError(typeof msg === 'string' ? msg : 'Erro ao salvar evolução.');
            setEstadoSalvamento('Falha ao salvar');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Anulação lógica do registro vigente, com a substituição em seguida.
     *
     * A API já suportava anular (DELETE /evolucoes/{id}, que não apaga nada) e
     * gravar uma substituta, mas nenhuma tela chamava isso: o registro errado
     * ficava no prontuário sem caminho de correção. Exige senha (autorização de
     * uso único), motivo e confirmação explícita.
     */
    const anularRegistro = async () => {
        if (!evolucaoSalva?.id || evolucaoSalva.anulada) return;

        const ok = await confirmAcao({
            title: 'Anular registro clínico',
            text: 'O conteúdo NÃO é apagado: fica no histórico, marcado como sem validade, '
                + 'e você registra uma evolução substituta em seguida.',
            confirmButtonText: 'Anular registro',
            cancelButtonText: 'Voltar',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        const motivo = await pedirMotivo({
            title: 'Motivo da anulação',
            text: 'Descreva por que este registro perde validade:',
            confirmButtonText: 'Anular',
        });
        if (!motivo) return;

        const operacao = `evolucao:${evolucaoSalva.id}:excluir`;
        const senha = await pedirSenha({
            text: 'Anular registro clínico exige confirmação de identidade. Digite sua senha:',
        });
        if (!senha) return;

        setSaving(true);
        try {
            await solicitarAutorizacao(operacao, senha);
            await api.delete(`/evolucoes/${evolucaoSalva.id}`, {
                data: { motivo },
                headers: { 'X-Autorizacao': consumirAutorizacao(operacao) || '' },
            });
            toastSuccess('Registro anulado. Escreva a evolução substituta.');
            setError('');
            setFetching(true);
            setRecarregar(n => n + 1);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data;
            setError(typeof msg === 'string' ? msg : 'Não foi possível anular o registro.');
        } finally {
            setSaving(false);
        }
    };

    if (fetching) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress sx={{ color: '#1ABB9C' }} />
        </Box>;
    }

    const formatarDataHora = (iso) => iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

    const ListaSecoes = (
        <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1ABB9C', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Registro da Sessão
                </Typography>
            </Box>
            <List disablePadding>
                <ListItemButton selected={secao === SECAO_EVOLUCAO} onClick={() => setSecao(SECAO_EVOLUCAO)}
                    sx={{ '&.Mui-selected': { bgcolor: '#1ABB9C15', borderLeft: '3px solid #1ABB9C' } }}>
                    <ListItemIcon sx={{ minWidth: 40 }}><NotesIcon sx={{ color: secao === SECAO_EVOLUCAO ? '#1ABB9C' : '#999' }} /></ListItemIcon>
                    <ListItemText primary="Evolução da Sessão" secondary="Anotações, técnicas, planejamento" />
                    {evolucaoPreenchida
                        ? <CheckCircleIcon sx={{ fontSize: '1.1rem', color: '#1ABB9C' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: '1.1rem', color: '#ccc' }} />}
                </ListItemButton>
                <Divider />
                <ListItemButton selected={secao === SECAO_RISCO} onClick={() => setSecao(SECAO_RISCO)}
                    sx={{ '&.Mui-selected': { bgcolor: '#1ABB9C15', borderLeft: '3px solid #1ABB9C' } }}>
                    <ListItemIcon sx={{ minWidth: 40 }}><WarningAmberIcon sx={{ color: secao === SECAO_RISCO ? '#dc2626' : '#999' }} /></ListItemIcon>
                    <ListItemText primary="Avaliação de Risco" secondary={`${avaliacoes.length} registro(s)`} />
                    {riscoPreenchido
                        ? <CheckCircleIcon sx={{ fontSize: '1.1rem', color: '#1ABB9C' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: '1.1rem', color: '#ccc' }} />}
                </ListItemButton>
            </List>
        </Paper>
    );

    const FormEvolucao = (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>Evolução da Sessão</Typography>
                <Typography variant="body2" color="text.secondary">
                    Anotações feitas durante a sessão. Conteúdo sensível, salvo criptografado.
                </Typography>
            </Box>
            <TextField fullWidth multiline minRows={6} label="Anotações" disabled={soLeitura}
                value={evolucao.anotacoes}
                onChange={(e) => setEvoCampo('anotacoes', e.target.value)} />
            <TextField fullWidth multiline minRows={3} label="Técnicas Utilizadas" disabled={soLeitura}
                value={evolucao.tecnicasUtilizadas}
                onChange={(e) => setEvoCampo('tecnicasUtilizadas', e.target.value)} />
            <TextField fullWidth multiline minRows={3} label="Planejamento da Próxima Sessão" disabled={soLeitura}
                value={evolucao.planejamentoProximaSessao}
                onChange={(e) => setEvoCampo('planejamentoProximaSessao', e.target.value)} />
        </Stack>
    );

    const FormRisco = (
        <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>Avaliação de Risco</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Fatores de risco identificados ao final da sessão. Múltiplas avaliações permitidas.
                    </Typography>
                </Box>
                {!soLeitura && (
                    <Button variant="contained" onClick={addAvaliacao}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                        Adicionar Avaliação
                    </Button>
                )}
            </Box>

            {avaliacoes.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                    <WarningAmberIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">Nenhuma avaliação registrada.</Typography>
                </Paper>
            )}

            {avaliacoes.map((a, idx) => {
                const nivel = NIVEL_RISCO_OPCOES.find(n => n.v === a.nivelRisco) || NIVEL_RISCO_OPCOES[0];
                return (
                    <Paper key={idx} variant="outlined" sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography fontWeight={700}>Avaliação #{idx + 1}</Typography>
                                <Chip size="small" label={nivel.l}
                                    sx={{ bgcolor: nivel.bg, color: nivel.color, fontWeight: 600 }} />
                            </Stack>
                            {!soLeitura && (
                                <Button size="small" color="error" onClick={() => removeAvaliacao(idx)}>Remover</Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                            <TextField fullWidth type="date" label="Data" disabled={soLeitura}
                                InputLabelProps={{ shrink: true }}
                                value={a.dataAvaliacao}
                                onChange={(e) => setAvCampo(idx, 'dataAvaliacao', e.target.value)} />
                            <TextField select fullWidth label="Nível de Risco" disabled={soLeitura}
                                value={a.nivelRisco}
                                onChange={(e) => setAvCampo(idx, 'nivelRisco', e.target.value)}>
                                {NIVEL_RISCO_OPCOES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 1 }}>
                            <FormControlLabel disabled={soLeitura}
                                control={<Checkbox checked={a.ideacaoSuicida} onChange={(e) => setAvCampo(idx, 'ideacaoSuicida', e.target.checked)} />}
                                label="Ideação Suicida" />
                            <FormControlLabel disabled={soLeitura}
                                control={<Checkbox checked={a.planoSuicida} onChange={(e) => setAvCampo(idx, 'planoSuicida', e.target.checked)} />}
                                label="Plano Suicida" />
                            <FormControlLabel disabled={soLeitura}
                                control={<Checkbox checked={a.ideacaoAutolesao} onChange={(e) => setAvCampo(idx, 'ideacaoAutolesao', e.target.checked)} />}
                                label="Ideação Autolesão" />
                            <FormControlLabel disabled={soLeitura}
                                control={<Checkbox checked={a.riscoParaTerceiros} onChange={(e) => setAvCampo(idx, 'riscoParaTerceiros', e.target.checked)} />}
                                label="Risco para Terceiros" />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Stack spacing={2}>
                            <TextField fullWidth multiline minRows={2} label="Conduta Adotada" disabled={soLeitura}
                                value={a.condutaAdotada}
                                onChange={(e) => setAvCampo(idx, 'condutaAdotada', e.target.value)} />
                            <TextField fullWidth multiline minRows={2} label="Encaminhamentos Realizados" disabled={soLeitura}
                                value={a.encaminhamentosRealizados}
                                onChange={(e) => setAvCampo(idx, 'encaminhamentosRealizados', e.target.value)} />
                            <TextField fullWidth multiline minRows={2} label="Rede Social Acionada" disabled={soLeitura}
                                value={a.redeSocialAcionada}
                                onChange={(e) => setAvCampo(idx, 'redeSocialAcionada', e.target.value)} />
                        </Stack>
                    </Paper>
                );
            })}
        </Stack>
    );

    return (
        <Box sx={{ flexGrow: 1, pb: soLeitura ? 4 : 10 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => { desativarFoco(); navigate('/sessoes'); }}
                    sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                        Registro de Evolução
                    </Typography>
                    {sessao && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: '#1ABB9C', fontSize: '0.75rem' }}>
                                {(sessao.nomePaciente || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" color="text.secondary">
                                {sessao.nomePaciente} — {formatarDataHora(sessao.dataHora)}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* C3: falha de carga não é "sessão sem evolução". Bloqueia a edição e
                oferece nova tentativa, para não salvar em cima do que não carregou. */}
            {falhaCarga && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => { setFalhaCarga(''); setFetching(true); setRecarregar(n => n + 1); }}
                        >
                            Tentar de novo
                        </Button>
                    }
                >
                    {falhaCarga} A edição está bloqueada para não sobrescrever o registro existente.
                </Alert>
            )}

            {conflito && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => { setConflito(false); setError(''); setFetching(true); setRecarregar(n => n + 1); }}
                        >
                            Recarregar do servidor
                        </Button>
                    }
                >
                    Outra pessoa gravou este registro depois que você abriu a tela.
                    <strong> O texto que você digitou continua aqui</strong> — copie o que
                    precisa antes de recarregar, porque recarregar traz a versão do servidor.
                </Alert>
            )}

            {evolucaoSalva?.anulada && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    Esta evolução foi anulada{evolucaoSalva.motivoAnulacao ? `: ${evolucaoSalva.motivoAnulacao}` : '.'}
                    {evolucaoSalva.anuladaPor ? ` (por ${evolucaoSalva.anuladaPor})` : ''}
                    {' '}O conteúdo anulado fica preservado no histórico abaixo. O que você
                    escrever agora será registrado como <strong>evolução substituta</strong>.
                </Alert>
            )}

            {/* Histórico: registro vigente e os anulados que ele substitui. */}
            {historico.length > 1 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Histórico de registros desta sessão</strong>
                    <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
                        {historico.map((h) => (
                            <li key={h.id}>
                                {h.vigente ? 'Vigente' : 'Anulada'}
                                {h.registradoPor ? ` · ${h.registradoPor}` : ''}
                                {h.dataRegistro ? ` · ${new Date(h.dataRegistro).toLocaleString('pt-BR')}` : ''}
                                {h.motivoAnulacao ? ` · motivo da anulação: ${h.motivoAnulacao}` : ''}
                                {h.idEvolucaoAnterior ? ` · substitui o registro #${h.idEvolucaoAnterior}` : ''}
                            </li>
                        ))}
                    </Box>
                </Alert>
            )}

            {evolucaoSalva && !evolucaoSalva.anulada && !evolucaoSalva.rascunho && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Atendimento finalizado
                    {evolucaoSalva.registradoPor ? ` por ${evolucaoSalva.registradoPor}` : ''}.
                    {evolucaoSalva.editadoPor
                        ? ` Última alteração por ${evolucaoSalva.editadoPor}${evolucaoSalva.motivoAlteracao ? ` — ${evolucaoSalva.motivoAlteracao}` : ''}.`
                        : ''}
                    {' '}Alterações exigem confirmação de identidade e motivo.
                </Alert>
            )}

            {evolucaoSalva?.rascunho && !evolucaoSalva?.anulada && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Rascunho: o atendimento ainda <strong>não</strong> foi finalizado.
                </Alert>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
                gap: 3,
                alignItems: 'flex-start',
            }}>
                <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                    {ListaSecoes}
                </Box>
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                    {secao === SECAO_EVOLUCAO ? FormEvolucao : FormRisco}
                </Paper>
            </Box>

            {!soLeitura && (
                <Paper elevation={3}
                    sx={{
                        position: 'fixed', bottom: 0,
                        left: modoFocoAtivo ? 0 : { xs: 0, md: 240 },
                        right: 0,
                        px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                        bgcolor: '#fff', borderTop: '1px solid #eee', zIndex: 10,
                    }}>
                    {estadoSalvamento && (
                        <Chip
                            size="small"
                            label={estadoSalvamento}
                            aria-live="polite"
                            sx={{
                                mr: 1,
                                bgcolor: alteracoesNaoSalvas ? '#fff4e5' : '#e8f5e9',
                                color: alteracoesNaoSalvas ? '#b45309' : '#1b5e20',
                                fontWeight: 600,
                            }}
                        />
                    )}
                    {modoFocoAtivo ? (
                        <Chip icon={<LockIcon />} label="Modo Foco ativo"
                            sx={{ bgcolor: '#1ABB9C15', color: '#1ABB9C', fontWeight: 600 }}
                            onDelete={desativarFoco}
                            deleteIcon={<VisibilityOffIcon />} />
                    ) : <Box />}
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={() => { desativarFoco(); navigate('/sessoes'); }}
                            sx={{ color: '#666', borderColor: '#ccc' }}>
                            Voltar
                        </Button>
                        {evolucaoSalva?.id && !evolucaoSalva.anulada && !evolucaoSalva.rascunho && !soLeitura && (
                            <Button variant="outlined" color="error" onClick={anularRegistro}
                                disabled={saving || !!falhaCarga}
                                startIcon={<WarningAmberIcon />}>
                                Anular registro
                            </Button>
                        )}
                        <Button variant="outlined" onClick={() => salvar(false)}
                            disabled={saving || !!falhaCarga}
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ color: '#1ABB9C', borderColor: '#1ABB9C' }}>
                            Salvar rascunho
                        </Button>
                        <Button variant="contained" onClick={() => salvar(true)}
                            disabled={saving || !!falhaCarga || !evolucaoPreenchida}
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                            sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 3 }}>
                            Finalizar atendimento
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Dialog open={countdownModal.open} disableEscapeKeyDown
                PaperProps={{ sx: { borderRadius: 3, minWidth: 360 } }}>
                <DialogTitle sx={{
                    bgcolor: '#1ABB9C', color: 'white', display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                    <LockIcon />
                    Entrando em Modo Evolução
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        A barra lateral será ocultada para dar foco total ao registro do prontuário.
                        Você poderá sair a qualquer momento pelo botão "Modo Foco ativo" no rodapé.
                    </Typography>
                    <Box sx={{ textAlign: 'center', my: 2 }}>
                        <Typography variant="h2" sx={{ fontWeight: 700, color: '#1ABB9C' }}>
                            {countdownModal.segundos}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            segundo{countdownModal.segundos === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={((3 - countdownModal.segundos) / 3) * 100}
                        sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: '#1ABB9C' } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => { setCountdownModal({ open: false, segundos: 0 }); }} color="inherit">
                        Pular foco
                    </Button>
                    <Button variant="contained" onClick={() => { setCountdownModal({ open: false, segundos: 0 }); ativarFoco(); }}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                        Entrar agora
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
